/* Editor del horario semanal de una agenda (TD-11766).
 *
 * Por cada responsable: qué días de la semana atiende y, en cada día, uno o
 * varios tramos. Varios tramos = horario con cortes (08:00-12:00 y 14:00-17:00
 * es un corte de 12 a 14). Cada tramo se divide en turnos según la duración y
 * la pausa; debajo de cada día se muestra cuántos turnos quedan.
 */
import { IonButton, IonIcon } from "@ionic/react";
import { TextField } from "@material-ui/core";
import { addOutline, copyOutline, trashOutline } from "ionicons/icons";
import { useEffect, useState } from "react";
import { FranjaForm } from "src/services/agenda";
import { aMinutos, DIAS_SEMANA, resumenHorario, turnosEnTramo } from "src/utils/agenda";

interface Props {
  franjas: FranjaForm[];
  onChange: (franjas: FranjaForm[]) => void;
  responsables: Array<{ _k: number; nombre: string; cedula?: string | null }>;
  duracion: number;
  pausa: number;
  nuevaClave: () => number;
}

const TRAMO_POR_DEFECTO = { hora_desde: "08:00", hora_hasta: "12:00" };

/** 'HH:MM' + minutos, sin pasar de 23:59. */
const sumarMinutos = (hhmm: string, minutos: number) => {
  const total = Math.min(aMinutos(hhmm) + minutos, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

/** Problemas de los tramos de un día (mismo responsable). */
export const erroresDelDia = (tramos: FranjaForm[], duracion: number): string[] => {
  const errores: string[] = [];
  const ordenados = [...tramos].sort((a, b) => a.hora_desde.localeCompare(b.hora_desde));
  ordenados.forEach((t, i) => {
    if (!t.hora_desde || !t.hora_hasta || t.hora_hasta <= t.hora_desde) {
      errores.push(`El tramo ${t.hora_desde || "?"}–${t.hora_hasta || "?"} termina antes de empezar`);
    } else if (aMinutos(t.hora_hasta) - aMinutos(t.hora_desde) < duracion) {
      errores.push(`El tramo ${t.hora_desde}–${t.hora_hasta} es más corto que un turno (${duracion} min)`);
    }
    const sig = ordenados[i + 1];
    if (sig && sig.hora_desde < t.hora_hasta) {
      errores.push(`Los tramos ${t.hora_desde}–${t.hora_hasta} y ${sig.hora_desde}–${sig.hora_hasta} se superponen`);
    }
  });
  return errores;
};

const AgendaHorarioSemanal: React.FC<Props> = ({ franjas, onChange, responsables, duracion, pausa, nuevaClave }) => {
  // Responsable cuyo horario se está editando (null = agenda sin responsables).
  const [actual, setActual] = useState<number | null>(responsables[0]?._k ?? null);

  useEffect(() => {
    if (!responsables.some((r) => r._k === actual)) setActual(responsables[0]?._k ?? null);
  }, [responsables, actual]);

  const propias = franjas.filter((f) => f.responsable_k === actual);
  // Sin ordenar: si la fila cambiara de lugar al editar una hora (o al vaciarla
  // mientras se escribe), el foco quedaría en un campo que se mueve a otra
  // posición. Vienen ordenados del backend y los nuevos se agregan al final.
  const tramosDe = (dia: number) => propias.filter((f) => f.dia_semana === dia);

  /** Reemplaza los tramos de un día del responsable actual. */
  const fijarDia = (dia: number, tramos: Array<{ hora_desde: string; hora_hasta: string; _k?: number }>) =>
    onChange([
      ...franjas.filter((f) => !(f.responsable_k === actual && f.dia_semana === dia)),
      ...tramos.map((t) => ({
        _k: t._k ?? nuevaClave(),
        dia_semana: dia,
        hora_desde: t.hora_desde,
        hora_hasta: t.hora_hasta,
        responsable_k: actual,
      })),
    ]);

  const activarDia = (dia: number, activo: boolean) => {
    if (!activo) return fijarDia(dia, []);
    // Al activar un día se copia el horario del último día activo (lo más habitual
    // es que todos los días hábiles tengan el mismo horario).
    const referencia = [...DIAS_SEMANA].reverse().map((d) => tramosDe(d.valor)).find((t) => t.length);
    fijarDia(dia, (referencia || [TRAMO_POR_DEFECTO]).map(({ hora_desde, hora_hasta }) => ({ hora_desde, hora_hasta })));
  };

  const agregarTramo = (dia: number) => {
    const tramos = tramosDe(dia);
    const ultimo = [...tramos].sort((a, b) => a.hora_hasta.localeCompare(b.hora_hasta)).pop();
    // El nuevo tramo empieza una hora después del último (el corte) y dura 3 horas.
    const desde = ultimo ? sumarMinutos(ultimo.hora_hasta, 60) : TRAMO_POR_DEFECTO.hora_desde;
    fijarDia(dia, [...tramos, { hora_desde: desde, hora_hasta: sumarMinutos(desde, 180) }]);
  };

  const editarTramo = (dia: number, k: number, cambio: Partial<FranjaForm>) =>
    fijarDia(dia, tramosDe(dia).map((t) => (t._k === k ? { ...t, ...cambio } : t)));

  const copiarATodos = (dia: number) => {
    const tramos = tramosDe(dia).map(({ hora_desde, hora_hasta }) => ({ hora_desde, hora_hasta }));
    const activos = DIAS_SEMANA.filter((d) => d.valor !== dia && tramosDe(d.valor).length).map((d) => d.valor);
    onChange([
      ...franjas.filter((f) => !(f.responsable_k === actual && activos.includes(f.dia_semana))),
      ...activos.flatMap((d) =>
        tramos.map((t) => ({ _k: nuevaClave(), dia_semana: d, ...t, responsable_k: actual }))
      ),
    ]);
  };

  const nombre = (r: Props["responsables"][number]) => r.nombre || r.cedula || "Responsable";
  const diasActivos = DIAS_SEMANA.filter((d) => tramosDe(d.valor).length).length;

  return (
    <div>
      {responsables.length > 1 && (
        <>
          <span className="agenda-label">Horario de</span>
          <div className="agenda-chips">
            {responsables.map((r) => (
              <button key={r._k} type="button" className={`agenda-chip${actual === r._k ? " activo" : ""}`}
                onClick={() => setActual(r._k)}>
                {nombre(r)}
                {franjas.some((f) => f.responsable_k === r._k) ? "" : " (sin horario)"}
              </button>
            ))}
          </div>
        </>
      )}

      {DIAS_SEMANA.map((d) => {
        const tramos = tramosDe(d.valor);
        const activo = tramos.length > 0;
        const errores = erroresDelDia(tramos, duracion);
        const turnos = errores.length
          ? 0
          : tramos.reduce((n, t) => n + turnosEnTramo(t.hora_desde, t.hora_hasta, duracion, pausa), 0);
        return (
          <div key={d.valor} className="agenda-item" style={{ background: activo ? "#fcfdff" : "#f8fafc" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <label className="agenda-switch" style={{ alignItems: "center" }}>
                <input type="checkbox" checked={activo} onChange={(e) => activarDia(d.valor, e.target.checked)} />
                <strong>{d.largo}</strong>
              </label>
              {activo ? (
                <span className="agenda-card-sub" style={{ margin: 0 }}>
                  {turnos} turno{turnos === 1 ? "" : "s"}
                </span>
              ) : (
                <span className="agenda-card-sub" style={{ margin: 0 }}>No atiende</span>
              )}
            </div>

            {activo && (
              <div style={{ marginTop: 10 }}>
                {tramos.map((t) => (
                  <div key={t._k} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <TextField size="small" label="Desde" type="time" variant="outlined"
                      value={t.hora_desde} InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }} style={{ flex: 1 }}
                      onChange={(e) => editarTramo(d.valor, t._k, { hora_desde: e.target.value })} />
                    <TextField size="small" label="Hasta" type="time"
                      variant="outlined" value={t.hora_hasta} InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }}
                      style={{ flex: 1 }} onChange={(e) => editarTramo(d.valor, t._k, { hora_hasta: e.target.value })} />
                    <IonButton fill="clear" size="small" color="medium" aria-label="Quitar tramo"
                      onClick={() => fijarDia(d.valor, tramos.filter((x) => x._k !== t._k))}>
                      <IonIcon slot="icon-only" icon={trashOutline} />
                    </IonButton>
                  </div>
                ))}
                {errores.map((e, i) => (
                  <div key={i} style={{ color: "#b91c1c", fontSize: 12, marginBottom: 4 }}>{e}</div>
                ))}
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <IonButton fill="clear" size="small" onClick={() => agregarTramo(d.valor)}>
                    <IonIcon slot="start" icon={addOutline} /> Agregar corte
                  </IonButton>
                  {diasActivos > 1 && (
                    <IonButton fill="clear" size="small" color="medium" onClick={() => copiarATodos(d.valor)}>
                      <IonIcon slot="start" icon={copyOutline} /> Copiar a los otros días marcados
                    </IonButton>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {propias.length > 0 && (
        <div className="agenda-aviso info" style={{ marginBottom: 0 }}>
          {responsables.length > 1 && actual !== null && (
            <strong>{nombre(responsables.find((r) => r._k === actual)!)}: </strong>
          )}
          {resumenHorario(propias)}
        </div>
      )}
    </div>
  );
};

export default AgendaHorarioSemanal;
